import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ExternalLink, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { connectorQueries } from '@/lib/api/queries/connector'
import {
  useExportPrdToDrive,
  useExportPrdToClickUp,
} from '@/lib/api/mutations/prd-draft'
import {
  ClickUpDocDestinationPicker,
  type ClickUpDocDestination,
} from './clickup-doc-destination-picker'
import {
  GoogleDriveDestinationPicker,
  type GoogleDriveDestination,
} from './google-drive-destination-picker'
import type { PrdDraft } from '@/lib/api/types'

interface PrdExportControlsProps {
  agentId: string
  draft: PrdDraft
}

export function PrdExportControls({ agentId, draft }: PrdExportControlsProps) {
  const { data: connectors } = useQuery(connectorQueries.list())
  const driveConnector = connectors?.find(
    (c) => c.type === 'google_drive' && c.status === 'active',
  )
  const clickUpConnector = connectors?.find(
    (c) => c.type === 'clickup' && c.status === 'active',
  )

  const driveExport = useExportPrdToDrive(agentId, draft.id)
  const clickUpExport = useExportPrdToClickUp(agentId, draft.id)

  const [showClickUpDialog, setShowClickUpDialog] = useState(false)
  const [destination, setDestination] = useState<ClickUpDocDestination | null>(null)

  const [showDriveDialog, setShowDriveDialog] = useState(false)
  const [driveDestination, setDriveDestination] =
    useState<GoogleDriveDestination | null>(null)

  const handleDriveSubmit = () => {
    if (!driveConnector || !driveDestination) return
    driveExport.mutate(
      {
        connectorId: driveConnector.id,
        folderId: driveDestination.folderId,
      },
      {
        onSuccess: () => {
          setShowDriveDialog(false)
          setDriveDestination(null)
        },
      },
    )
  }

  const handleDriveOpenChange = (open: boolean) => {
    setShowDriveDialog(open)
    if (!open) setDriveDestination(null)
  }

  const handleClickUpSubmit = () => {
    if (!clickUpConnector || !destination) return
    clickUpExport.mutate(
      {
        connectorId: clickUpConnector.id,
        workspaceId: destination.workspaceId,
        parentId: destination.parentId,
        parentType: destination.parentType,
      },
      {
        onSuccess: () => {
          setShowClickUpDialog(false)
          setDestination(null)
        },
      },
    )
  }

  const handleClickUpOpenChange = (open: boolean) => {
    setShowClickUpDialog(open)
    if (!open) setDestination(null)
  }

  return (
    <div className="flex flex-col gap-2 p-4 border border-border rounded-lg bg-card">
      <h3 className="text-sm font-medium">Export</h3>

      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowDriveDialog(true)}
            disabled={!driveConnector || driveExport.isPending}
            className="flex-1 justify-start"
          >
            {driveExport.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Export to Google Drive
          </Button>
          {draft.driveFileUrl && (
            <a
              href={draft.driveFileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-primary"
              aria-label="Open in Google Drive"
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          )}
        </div>
        {!driveConnector && (
          <p className="text-xs text-muted-foreground">
            Connect Google Drive in Settings to enable.
          </p>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowClickUpDialog(true)}
            disabled={!clickUpConnector || clickUpExport.isPending}
            className="flex-1 justify-start"
          >
            {clickUpExport.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Export to ClickUp
          </Button>
          {draft.clickUpTaskUrl && (
            <a
              href={draft.clickUpTaskUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-primary"
              aria-label="Open in ClickUp"
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          )}
        </div>
        {!clickUpConnector && (
          <p className="text-xs text-muted-foreground">
            Connect ClickUp in Settings to enable.
          </p>
        )}
      </div>

      <Dialog open={showDriveDialog} onOpenChange={handleDriveOpenChange}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Export to Google Drive</DialogTitle>
            <DialogDescription>
              Pick a folder in your Drive. We&apos;ll create a Google Doc with
              this PRD&apos;s contents — Era logo at the top, full markdown
              converted to native Doc formatting.
            </DialogDescription>
          </DialogHeader>

          {driveConnector && (
            <GoogleDriveDestinationPicker
              connectorId={driveConnector.id}
              selected={driveDestination}
              onSelect={setDriveDestination}
            />
          )}

          <DialogFooter className="flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
            <span className="text-xs text-muted-foreground truncate">
              {driveDestination ? (
                <>
                  Destination:{' '}
                  <span className="text-foreground">{driveDestination.label}</span>
                </>
              ) : (
                'No folder selected'
              )}
            </span>
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" onClick={() => handleDriveOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleDriveSubmit}
                disabled={!driveDestination || driveExport.isPending}
              >
                {driveExport.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create Doc
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showClickUpDialog} onOpenChange={handleClickUpOpenChange}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Export to ClickUp</DialogTitle>
            <DialogDescription>
              Pick a Space, Folder, or List in ClickUp. We&apos;ll create a Doc
              with this PRD&apos;s contents inside the destination you choose.
            </DialogDescription>
          </DialogHeader>

          {clickUpConnector && (
            <ClickUpDocDestinationPicker
              connectorId={clickUpConnector.id}
              selected={destination}
              onSelect={setDestination}
            />
          )}

          <DialogFooter className="flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
            <span className="text-xs text-muted-foreground truncate">
              {destination ? (
                <>
                  Destination: <span className="text-foreground">{destination.label}</span>{' '}
                  <span className="capitalize">({destination.parentType})</span>
                </>
              ) : (
                'No destination selected'
              )}
            </span>
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" onClick={() => handleClickUpOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleClickUpSubmit}
                disabled={!destination || clickUpExport.isPending}
              >
                {clickUpExport.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create Doc
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
